import { ChartInfoKind } from '@frontend/chart/types';
import { CommonMessage } from '@frontend/lang';
import { useMemo } from 'react';
import { LuTrendingDown, LuTrendingUp } from 'react-icons/lu';
import { FormattedMessage, useIntl } from 'react-intl';

import { useLast60DaysVendingProductPopularity } from '../../../statistics/vending/use-last-60-days-vending-product-popularity';
import { VendingProductPopularityItem, takeVendingProductPopularityRisersAndFallers } from '../../../statistics/vending/vending-product-popularity';
import { InsightCardWidth } from '../insight-card-width';
import { InsightCard } from '../insight-card/insight-card.component';
import { InsightId } from '../insight-registry/insight-ids';

export interface VendingProductPopularityListProps {
    accountId: string;
    width?: InsightCardWidth;
}

type PopularityRow = { type: 'section'; label: React.ReactNode } | { type: 'item'; item: VendingProductPopularityItem; direction: 'up' | 'down' };

const trendClassName = (direction: 'up' | 'down'): string => (direction === 'up' ? 'text-status-success' : 'text-status-warning');

export const VendingProductPopularityList = ({ accountId, width }: VendingProductPopularityListProps) => {
    const { formatMessage } = useIntl();
    const { items, isLoading } = useLast60DaysVendingProductPopularity(accountId);

    const previousHeader = formatMessage({
        id: 'platform.analytics.insights.vending-product-popularity-list.previous',
        description: 'Column header for previous-month package volume on the vending product popularity insights list',
        defaultMessage: 'Previous'
    });
    const currentHeader = formatMessage({
        id: 'platform.analytics.insights.vending-product-popularity-list.current',
        description: 'Column header for current-month package volume on the vending product popularity insights list',
        defaultMessage: 'Current'
    });
    const changeHeader = formatMessage({
        id: 'platform.statistics.vending.last-60-days-vending-package-volume-by-product-monthly-list-chart.change',
        description: 'Column header for the month-over-month percentage change in the Product popularity list on the vending dashboard',
        defaultMessage: 'Change'
    });

    const rows = useMemo((): PopularityRow[] => {
        const { risers, fallers } = takeVendingProductPopularityRisersAndFallers(items);
        const result: PopularityRow[] = [];

        if (risers.length > 0) {
            result.push({
                type: 'section',
                label: (
                    <FormattedMessage
                        id='platform.statistics.vending.last-60-days-vending-package-volume-by-product-monthly-list-chart.risers'
                        description='Section heading above the products with the highest month-over-month consumption increase on the vending dashboard'
                        defaultMessage='Risers'
                    />
                )
            });
            result.push(...risers.map((item) => ({ type: 'item' as const, item, direction: 'up' as const })));
        }

        if (fallers.length > 0) {
            result.push({
                type: 'section',
                label: (
                    <FormattedMessage
                        id='platform.statistics.vending.last-60-days-vending-package-volume-by-product-monthly-list-chart.fallers'
                        description='Section heading above the products with the largest month-over-month consumption decrease on the vending dashboard'
                        defaultMessage='Fallers'
                    />
                )
            });
            result.push(...fallers.map((item) => ({ type: 'item' as const, item, direction: 'down' as const })));
        }

        return result;
    }, [items]);

    return (
        <InsightCard
            insightId={InsightId.VENDING_PRODUCT_POPULARITY}
            infoKind={ChartInfoKind.VENDING_PRODUCT_POPULARITY_LIST}
            width={width}
            title={
                <FormattedMessage
                    id='platform.statistics.vending.last-60-days-vending-package-volume-by-product-monthly-list-chart.title'
                    description='Title of the vending dashboard list showing products with the largest month-over-month consumption increase and decrease'
                    defaultMessage='Product popularity'
                />
            }
            subtitle={
                <FormattedMessage
                    id='platform.statistics.pudo.last-60-days-pudo-timer-duration-metric-chart-card.subtitle'
                    description='Subtitle on PuDo dashboard cards comparing average drop-off or pick-up time to the previous month'
                    defaultMessage='Compared to last month'
                />
            }
            isLoading={isLoading}>
            {rows.length === 0 ? (
                <div className='text-foreground-default'>{CommonMessage.ERRORS.NO_DATA_AVAILABLE}</div>
            ) : (
                <div className='max-h-[420px] w-full min-w-0 overflow-y-auto'>
                    <div className='border-border-default text-foreground-default flex items-start gap-4 border-b text-base font-medium'>
                        <div className='w-full px-1 py-2'>{CommonMessage.OBJECTS.PRODUCT.DEFAULT}</div>
                        <div className='w-[88px] shrink-0 py-2 text-right'>{previousHeader}</div>
                        <div className='w-[88px] shrink-0 py-2 text-right'>{currentHeader}</div>
                        <div className='w-[100px] shrink-0 py-2 text-right'>{changeHeader}</div>
                    </div>
                    {rows.map((row, index) =>
                        row.type === 'section' ? (
                            <div
                                key={`section-${index}`}
                                className='border-border-default text-foreground-default border-b px-1 py-3 text-base font-semibold'>
                                {row.label}
                            </div>
                        ) : (
                            <div
                                key={row.item.key}
                                className='border-border-default flex h-11 items-center gap-4 border-b'>
                                <div className='text-foreground-default flex w-full min-w-0 items-center px-1 text-sm font-medium'>{row.item.name}</div>
                                <div className='text-foreground-default w-[88px] shrink-0 text-right text-sm font-medium'>{row.item.previous}</div>
                                <div className='text-foreground-default w-[88px] shrink-0 text-right text-sm font-medium'>{row.item.current}</div>
                                <div
                                    className={`flex w-[100px] shrink-0 items-center justify-end gap-1 text-sm font-semibold ${trendClassName(row.direction)}`}>
                                    {row.direction === 'up' ? <LuTrendingUp className='size-5' /> : <LuTrendingDown className='size-5' />}
                                    {row.item.percentChange}%
                                </div>
                            </div>
                        )
                    )}
                </div>
            )}
        </InsightCard>
    );
};
